import { useExperiments, useTranslation } from '@wix/yoshi-flow-editor';
import { useSettings, useStyles } from '@wix/tpa-settings/react';

import {
  ITEM_LAYOUT_TYPE,
  QUICK_ADD_BUTTON_CONTENT,
  QUICK_ADD_BUTTON_WIDTH,
  QUICK_ADD_ICON_TYPE,
  QUICK_ADD_HORIZONTAL_ALIGNMENT,
} from '../../../constants';
import settingsParams from '../../../settingsParams';
import { addToCartButtonStylesParams } from '../../../Settings/Tabs/Design/AddToCartButton/stylesParams';
import { QUICK_ADD_ICON_TYPES } from '../../../Settings/Tabs/Design/AddToCartButton/constants';
import { EXPERIMENTS } from '../../../../../utils/experiments';

import { ALIGNMENT_NAME } from './constants';

const ICON_BY_TYPE = new Map<
  QUICK_ADD_ICON_TYPE,
  (typeof QUICK_ADD_ICON_TYPES)[number]['Icon']
>(QUICK_ADD_ICON_TYPES.map(({ value, Icon }) => [value, Icon]));

export const useAddToCartControlHooks = (placement: 'itemInfo' | 'onImage') => {
  const { t } = useTranslation();
  const { experiments } = useExperiments();
  const styles = useStyles();
  const settings = useSettings();
  const isQuickAddToCartEnabled = experiments.enabled(
    EXPERIMENTS.QUICK_ADD_TO_CART_BUTTON,
  );

  const content = styles.get(addToCartButtonStylesParams.quickAddButtonContent);
  const widthMode = styles.get(addToCartButtonStylesParams.quickAddButtonWidth);
  // `styles.get` only knows these are generic Number style params — the
  // stored values are always one of our enums' members at runtime.
  const iconType = styles.get(
    addToCartButtonStylesParams.quickAddIconType,
  ) as QUICK_ADD_ICON_TYPE;
  const horizontalAlignment = styles.get(
    addToCartButtonStylesParams.quickAddHorizontalAlignment,
  ) as QUICK_ADD_HORIZONTAL_ALIGNMENT;

  const Icon =
    ICON_BY_TYPE.get(iconType) ?? ICON_BY_TYPE.get(QUICK_ADD_ICON_TYPE.PLUS)!;

  const label = settings.get(settingsParams.addToCartButtonText);

  const isCardLayout =
    settings.get(settingsParams.itemLayoutType) === ITEM_LAYOUT_TYPE.CARD;

  const showIcon = content !== QUICK_ADD_BUTTON_CONTENT.TEXT;
  const showAddToCartButtonText = content !== QUICK_ADD_BUTTON_CONTENT.ICON;

  // Icon-only buttons are always fixed-to-text — width isn't user-controllable
  // for this content type (see Settings/Tabs/Design/AddToCartButton/index.tsx).
  const isStretched =
    showAddToCartButtonText && widthMode === QUICK_ADD_BUTTON_WIDTH.STRETCHED;

  /**
   * When the button is stretched we need to align the counter correctly.
   * Since it doesn't stretch, we align it depending on placement: when onImage -- to the right,
   * when on content -- to the left
   *
   * Whn we have content for icon and item info placement -- we align to the right
   *
   * Otherwise we align to chosen setting or to the left by default
   */
  const getAlignmentName = () => {
    // to align counter
    if (isStretched) {
      return placement === 'onImage' ? 'right' : 'left';
    }

    if (content === QUICK_ADD_BUTTON_CONTENT.ICON && placement === 'itemInfo') {
      return 'right';
    }

    return ALIGNMENT_NAME[horizontalAlignment] ?? 'left';
  };

  return {
    isQuickAddToCartEnabled,
    Icon,
    label,
    isStretched,
    isCardLayout,
    showIcon,
    showAddToCartButtonText,
    alignmentName: getAlignmentName(),
    addAriaLabel: t('menu_olo.addToCartButton.ariaLabel'),
    removeAriaLabel: t('menu_olo.removeFromCartButton.ariaLabel'),
    quantityAriaLabel: t('menu_olo.addToCartButton.quantityAriaLabel'),
  };
};
